import React, { useState, useEffect } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Plus, DollarSign, Calculator, Package, Eye, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';
import cortesPdfService from '../services/cortesPdfService';
import inventoryService from '../services/inventoryService';
import projectBudgetService from '../services/projectBudgetService';
import LoadingSpinner from './LoadingSpinner';

const CortesPdfToBudget = ({ projectId, onClose, onItemAdded }) => {
  const [step, setStep] = useState('upload'); // upload, preview, product, item
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [searchingInventory, setSearchingInventory] = useState(false);
  const [inventoryProduct, setInventoryProduct] = useState(null);
  const [creatingItem, setCreatingItem] = useState(false);
  const [itemData, setItemData] = useState({
    item_name: '',
    category: 'material',
    description: '',
    quantity: 1,
    unit: 'ch',
    unit_price: 0,
    unit_cost: 0
  });

  // Constantes para cálculo
  const CHAPA_AREA_M2 = 2.75 * 1.85; // Área padrão de uma chapa: 2.75m × 1.85m = 5.0875m²

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      return;
    }

    if (selectedFile.type !== 'application/pdf') {
      toast.error('Apenas arquivos PDF são permitidos');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 10MB');
      return;
    }

    setFile(selectedFile);
    setPreview({
      name: selectedFile.name,
      size: (selectedFile.size / 1024 / 1024).toFixed(2) + ' MB'
    });
  };

  const processPdf = async () => {
    if (!file) {
      toast.error('Selecione um arquivo PDF');
      return;
    }

    setLoading(true);
    setStep('processing');

    try {
      // 1. Processar o PDF
      const processResponse = await cortesPdfService.processPdf(file);
      toast.success('PDF processado com sucesso!');

      // 2. Buscar os detalhes completos (como faz a tela de custos)
      // O processPdf retorna apenas { id, filename, total_cortes, cortes }
      // Precisamos dos dados_extras que só vêm no getPdfDetalhes
      const pdfId = processResponse.data?.id || processResponse.data?.data?.id;

      if (!pdfId) {
        throw new Error('ID do PDF não retornado pelo servidor');
      }

      // Buscar detalhes completos
      const detalhesResponse = await cortesPdfService.getPdfDetalhes(pdfId);
      const fullData = detalhesResponse.data;

      setPdfData(fullData);

      // Preparar dados do item baseados no que foi extraído
      const dadosExtras = fullData?.dados_extras || {};
      const planos = dadosExtras?.planos || [];
      const material = dadosExtras?.material || '';
      const espessura = dadosExtras?.espessura_mm || '';
      const cor = dadosExtras?.cor || '';
      const qtdChapas = dadosExtras?.quantidade_chapas || 0;

      // Calcular aproveitamento médio ponderado
      const totalChapasPlanos = planos.reduce((total, plano) => total + (plano.qtd || 0), 0);
      const aproveitamentoMedio = totalChapasPlanos > 0
        ? planos.reduce((total, plano) => total + ((plano.qtd || 0) * (plano.aproveitamento || 0)), 0) / totalChapasPlanos
        : 0;

      // Criar descrição do material
      const materialDesc = material ? `${material} ${cor} ${espessura}mm`.trim() : 'Chapa de MDF';

      setItemData(prev => ({
        ...prev,
        item_name: materialDesc || 'Chapa de MDF',
        description: `Cortes PDF: ${fullData.pdf_filename || fullData.filename} | ${qtdChapas} chapas | ${aproveitamentoMedio.toFixed(2)}% aproveitamento`,
        quantity: qtdChapas || 1,
        unit: 'ch'
      }));

      setStep('preview');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao processar PDF';
      toast.error(errorMessage);
      console.error('Erro ao processar PDF:', error);
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPreview = async () => {
    // Após confirmar a visualização, buscar no estoque
    const dadosExtras = pdfData?.dados_extras || {};
    const materialDesc = dadosExtras?.material
      ? `${dadosExtras.material} ${dadosExtras.cor || ''} ${dadosExtras.espessura_mm || ''}mm`.trim()
      : '';

    await searchInventory(materialDesc, dadosExtras?.espessura_mm);
    setStep('product');
  };

  const searchInventory = async (materialDesc, espessura) => {
    setSearchingInventory(true);

    try {
      const dadosExtras = pdfData?.dados_extras || {};

      // Tentar buscar por diferentes critérios
      const searchTerms = [
        materialDesc,
        `MDF ${dadosExtras?.cor || ''} ${espessura}mm`,
        `${espessura}mm`,
        'MDF'
      ].filter(Boolean);

      let foundProduct = null;

      for (const term of searchTerms) {
        try {
          const response = await inventoryService.getVariantsForBudget({
            search: term,
            ativo: true,
            limit: 5
          });

          const variants = response.data || [];
          if (variants.length > 0) {
            foundProduct = variants[0];
            break;
          }
        } catch (err) {
          console.warn(`Busca por "${term}" não encontrou resultados`);
        }
      }

      if (foundProduct) {
        setInventoryProduct(foundProduct);

        // Calcular preço baseado na área útil calculada corretamente
        const precoPorM2 = parseFloat(foundProduct.preco_venda) || 0;
        const precoCusto = parseFloat(foundProduct.preco_custo) || 0;

        // Preço baseado na área útil real (já calculada com os planos individuais)
        const precoTotal = areaUtil * precoPorM2;

        setItemData(prev => ({
          ...prev,
          item_name: foundProduct.produto_nome,
          unit_price: precoTotal / prev.quantity,
          unit_cost: precoCusto > 0 ? (precoCusto * areaUtil) / prev.quantity : 0,
          description: `${foundProduct.medida_text || ''} | Cortes PDF | ${qtdChapas} chapas | ${aproveitamentoMedio.toFixed(2)}% aproveitamento`
        }));

        toast.success('Produto encontrado no estoque!');
      } else {
        setInventoryProduct(null);
        toast.error('Material não encontrado no estoque');
      }
    } catch (error) {
      console.error('Erro ao buscar no estoque:', error);
    } finally {
      setSearchingInventory(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!itemData.item_name) {
      toast.error('Informe o nome do produto');
      return;
    }

    setCreatingItem(true);

    try {
      const dadosExtras = pdfData?.dados_extras || {};

      // Criar produto no estoque
      const productResponse = await inventoryService.createProduct({
        nome: itemData.item_name,
        categoria_id: 1,
        descricao: itemData.description,
        ativo: true
      });

      const productId = productResponse.data?.id || productResponse.data?.data?.id;

      if (productId) {
        const variantResponse = await inventoryService.createVariant(productId, {
          sku_variacao: `MDF-${Date.now()}`,
          preco_venda: itemData.unit_price,
          preco_custo: itemData.unit_cost,
          espessura_mm: dadosExtras?.espessura_mm ? parseFloat(dadosExtras.espessura_mm) : 18,
          cor: dadosExtras?.cor || '',
          medida_text: itemData.description
        });

        const variantId = variantResponse.data?.id || variantResponse.data?.data?.id;

        if (variantId) {
          setInventoryProduct({
            produto_id: productId,
            variant_id: variantId,
            produto_nome: itemData.item_name,
            preco_venda: itemData.unit_price,
            preco_custo: itemData.unit_cost
          });

          toast.success('Produto criado no estoque!');
        }
      }
    } catch (error) {
      console.error('Erro ao criar produto:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar produto no estoque');
    } finally {
      setCreatingItem(false);
    }
  };

  const handleAddToBudget = async () => {
    if (!itemData.item_name || itemData.unit_price <= 0) {
      toast.error('Preencha os dados do item corretamente');
      return;
    }

    try {
      const submitData = {
        item_name: itemData.item_name,
        category: itemData.category,
        description: itemData.description,
        quantity: itemData.quantity,
        unit: itemData.unit,
        unit_price: parseFloat(itemData.unit_price) || 0,
        unit_cost: parseFloat(itemData.unit_cost) || parseFloat(itemData.unit_price) || 0
      };

      await projectBudgetService.createBudgetItem(projectId, submitData);

      toast.success('Item adicionado ao orçamento com sucesso!');

      if (onItemAdded) {
        onItemAdded();
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Erro ao adicionar ao orçamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao adicionar item ao orçamento');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const calculateTotal = () => {
    return (itemData.quantity || 0) * (itemData.unit_price || 0);
  };

  const calculateTotalCost = () => {
    return (itemData.quantity || 0) * (itemData.unit_cost || 0);
  };

  // Dados extras do PDF processado
  const dadosExtras = pdfData?.dados_extras || {};

  // Calcular Área Útil usando os valores individuais de cada plano
  const planos = dadosExtras?.planos || [];
  const areaUtil = planos.reduce((total, plano) => {
    const qtd = plano.qtd || 0;
    const aproveitamento = plano.aproveitamento || 0;
    return total + (qtd * aproveitamento / 100 * CHAPA_AREA_M2);
  }, 0);

  // Calcular aproveitamento médio ponderado pela quantidade de chapas
  const totalChapasPlanos = planos.reduce((total, plano) => total + (plano.qtd || 0), 0);
  const aproveitamentoMedio = totalChapasPlanos > 0
    ? planos.reduce((total, plano) => total + ((plano.qtd || 0) * (plano.aproveitamento || 0)), 0) / totalChapasPlanos
    : 0;

  const qtdChapas = dadosExtras?.quantidade_chapas || 0;
  const areaTotalChapas = qtdChapas * CHAPA_AREA_M2;

  // ================== TELA DE UPLOAD ==================
  if (step === 'upload') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Upload className="w-6 h-6" />
            Importar Cortes PDF
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          )}
        </div>

        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Como funciona:</strong> Importe um PDF de plano de corte para automaticamente
            calcular o custo de chapas de MDF baseado nas informações do arquivo.
          </p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); processPdf(); }} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecione o arquivo PDF do plano de corte
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="pdf-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                  >
                    <span>Clique para selecionar</span>
                    <input
                      id="pdf-upload"
                      name="pdf-upload"
                      type="file"
                      accept=".pdf,application/pdf"
                      className="sr-only"
                      onChange={handleFileChange}
                      disabled={loading}
                    />
                  </label>
                  <p className="pl-1">ou arraste e solte</p>
                </div>
                <p className="text-xs text-gray-500">PDF até 10MB</p>
              </div>
            </div>

            {preview && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{preview.name}</p>
                    <p className="text-xs text-gray-500">{preview.size}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setPreview(null);
                      document.getElementById('pdf-upload').value = '';
                    }}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remover
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={!file || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <LoadingSpinner className="mr-2" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Processar PDF
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ================== TELA DE PROCESSAMENTO ==================
  if (step === 'processing') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-12 max-w-2xl w-full text-center">
        <LoadingSpinner className="mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Processando PDF...</h2>
        <p className="text-gray-600">Extraindo informações do plano de corte</p>
      </div>
    );
  }

  // ================== TELA DE PREVIEW (Dados Extraídos) ==================
  if (step === 'preview') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Eye className="w-6 h-6" />
            Dados Extraídos do PDF
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          )}
        </div>

        {/* Cabeçalho */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-gray-800">{pdfData?.pdf_filename}</h3>
              <p className="text-sm text-gray-500">
                Processado em {formatDate(pdfData?.data_importacao)}
              </p>
            </div>
          </div>
        </div>

        {/* Informações do Pedido */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="text-lg font-bold text-gray-800 mb-3">Informações do Pedido</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {dadosExtras?.codigo_pedido && (
              <div>
                <span className="text-sm font-medium text-gray-600">Código:</span>
                <p className="text-lg font-bold text-gray-800">{dadosExtras.codigo_pedido}</p>
              </div>
            )}
            {dadosExtras?.descricao_pedido && (
              <div className="md:col-span-2">
                <span className="text-sm font-medium text-gray-600">Descrição:</span>
                <p className="text-lg font-bold text-gray-800">{dadosExtras.descricao_pedido}</p>
              </div>
            )}
            {dadosExtras?.material && (
              <div>
                <span className="text-sm font-medium text-gray-600">Material:</span>
                <p className="text-lg font-bold text-gray-800">{dadosExtras.material}</p>
              </div>
            )}
            {dadosExtras?.espessura_mm && (
              <div>
                <span className="text-sm font-medium text-gray-600">Espessura:</span>
                <p className="text-lg font-bold text-gray-800">{dadosExtras.espessura_mm} mm</p>
              </div>
            )}
            {dadosExtras?.cor && (
              <div>
                <span className="text-sm font-medium text-gray-600">Cor:</span>
                <p className="text-lg font-bold text-gray-800">{dadosExtras.cor}</p>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-600">Qtd. Chapas:</span>
              <p className="text-lg font-bold text-blue-600">{qtdChapas}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Qtd. Peças:</span>
              <p className="text-lg font-bold text-gray-800">{dadosExtras?.quantidade_pecas || '-'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Aproveitamento:</span>
              <p className="text-lg font-bold text-green-600">{aproveitamentoMedio.toFixed(2)}%</p>
            </div>
          </div>
        </div>

        {/* Resumo de Áreas */}
        <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mb-6">
          <h4 className="text-md font-bold text-gray-800 mb-3">Resumo de Chapas</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <span className="text-sm text-gray-600">Qtd. Chapas</span>
              <p className="text-2xl font-bold text-gray-800">{qtdChapas}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Área Total</span>
              <p className="text-2xl font-bold text-blue-600">{areaTotalChapas.toFixed(2)} m²</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Área Útil</span>
              <p className="text-2xl font-bold text-green-600">{areaUtil.toFixed(2)} m²</p>
            </div>
          </div>
        </div>

        {/* Planos de Corte */}
        {dadosExtras?.planos && dadosExtras.planos.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <h4 className="text-lg font-bold text-gray-800 mb-3">Planos de Corte</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg border border-green-300">
                <thead className="bg-green-100">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-gray-700">Plano</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700">Qtd</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700">Peças</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700">Sobras</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700">Cortes</th>
                    <th className="px-3 py-2 text-center text-xs font-bold text-gray-700">Aprov.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-200">
                  {dadosExtras.planos.map((plano, idx) => (
                    <tr key={idx} className="hover:bg-green-50">
                      <td className="px-3 py-2 text-sm font-bold text-gray-800">
                        {plano.numero}/{plano.total || '?'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700">
                        {plano.qtd !== undefined ? plano.qtd : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700 font-semibold">
                        {plano.pecas !== undefined ? plano.pecas : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700">
                        {plano.sobras !== undefined ? plano.sobras : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center text-gray-700">
                        {plano.cortes !== undefined ? plano.cortes : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-center">
                        {plano.aproveitamento !== undefined ? (
                          <span className="font-bold text-green-600">{plano.aproveitamento.toFixed(2)}%</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              setStep('upload');
              setPdfData(null);
            }}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar
          </button>
          <button
            onClick={handleConfirmPreview}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Confirmar e Continuar
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // ================== TELA DE SELEÇÃO DE PRODUTO ==================
  if (step === 'product') {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6" />
            Verificação no Estoque
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          )}
        </div>

        {/* Resumo do PDF */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <span className="text-sm text-gray-600">Material</span>
              <p className="text-lg font-bold text-gray-800">{dadosExtras?.material || '-'} {dadosExtras?.cor || ''}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Espessura</span>
              <p className="text-lg font-bold text-gray-800">{dadosExtras?.espessura_mm ? dadosExtras.espessura_mm + 'mm' : '-'}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Chapas</span>
              <p className="text-lg font-bold text-blue-600">{qtdChapas}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600">Aproveitamento</span>
              <p className="text-lg font-bold text-green-600">{aproveitamentoMedio.toFixed(2)}%</p>
            </div>
          </div>
        </div>

        {/* Status do Estoque */}
        {searchingInventory ? (
          <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
            <LoadingSpinner className="mr-2" />
            <span>Buscando no estoque...</span>
          </div>
        ) : inventoryProduct ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-800">Produto encontrado no estoque!</span>
            </div>
            <div className="text-lg text-green-700 mb-2">
              {inventoryProduct.produto_nome}
            </div>
            {inventoryProduct.preco_venda && (
              <div className="text-green-700">
                Preço de venda: {formatCurrency(inventoryProduct.preco_venda)}/m²
              </div>
            )}
          </div>
        ) : (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-orange-600" />
              <span className="font-semibold text-orange-800">Material não encontrado no estoque</span>
            </div>
            <p className="text-orange-700 mb-4">
              Você pode criar o produto agora ou informar os valores manualmente.
            </p>
            <button
              onClick={handleCreateProduct}
              disabled={creatingItem}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {creatingItem ? (
                <LoadingSpinner className="mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Criar Produto no Estoque
            </button>
          </div>
        )}

        {/* Ações */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setStep('preview')}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Voltar
          </button>
          <button
            onClick={() => setStep('item')}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Continuar para Item
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    );
  }

  // ================== TELA DE DADOS DO ITEM ==================
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <DollarSign className="w-6 h-6" />
          Item do Orçamento
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        )}
      </div>

      {/* Resumo do Material */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-semibold text-blue-800 mb-2">Material do PDF</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <span className="text-xs text-gray-600">Material</span>
            <p className="font-bold text-gray-800">{dadosExtras?.material || '-'} {dadosExtras?.cor || ''}</p>
          </div>
          <div>
            <span className="text-xs text-gray-600">Espessura</span>
            <p className="font-bold text-gray-800">{dadosExtras?.espessura_mm ? dadosExtras.espessura_mm + 'mm' : '-'}</p>
          </div>
          <div>
            <span className="text-xs text-gray-600">Chapas</span>
            <p className="font-bold text-blue-600">{qtdChapas}</p>
          </div>
          <div>
            <span className="text-xs text-gray-600">Área Útil</span>
            <p className="font-bold text-green-600">{areaUtil.toFixed(2)} m²</p>
          </div>
        </div>
      </div>

      {/* Formulário */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Item *
          </label>
          <input
            type="text"
            value={itemData.item_name}
            onChange={(e) => setItemData({ ...itemData, item_name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Ex: MDF Branco 18mm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Categoria
          </label>
          <select
            value={itemData.category}
            onChange={(e) => setItemData({ ...itemData, category: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="material">Material</option>
            <option value="mão_de_obra">Mão de Obra</option>
            <option value="serviço">Serviço</option>
            <option value="outro">Outro</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Quantidade (chapas)
          </label>
          <input
            type="number"
            value={itemData.quantity}
            onChange={(e) => setItemData({ ...itemData, quantity: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unidade
          </label>
          <select
            value={itemData.unit}
            onChange={(e) => setItemData({ ...itemData, unit: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="ch">Chapas (ch)</option>
            <option value="m²">Metro quadrado (m²)</option>
            <option value="un">Unidade (un)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preço Unitário de Venda *
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={itemData.unit_price}
            onChange={(e) => setItemData({ ...itemData, unit_price: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preço Unitário de Custo
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={itemData.unit_cost}
            onChange={(e) => setItemData({ ...itemData, unit_cost: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descrição
          </label>
          <textarea
            value={itemData.description}
            onChange={(e) => setItemData({ ...itemData, description: e.target.value })}
            rows="2"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Descrição do item..."
          />
        </div>
      </div>

      {/* Totais */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-gray-600">Preço Total de Venda:</span>
          <span className="text-xl font-bold text-green-600">{formatCurrency(calculateTotal())}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Preço Total de Custo:</span>
          <span className="text-lg font-semibold text-red-600">{formatCurrency(calculateTotalCost())}</span>
        </div>
        {calculateTotalCost() > 0 && calculateTotal() > 0 && (
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-300">
            <span className="text-gray-600">Margem de Lucro:</span>
            <span className="text-lg font-semibold text-blue-600">
              {(((calculateTotal() - calculateTotalCost()) / calculateTotal()) * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <button
          onClick={() => setStep('product')}
          className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Voltar
        </button>
        <button
          onClick={handleAddToBudget}
          disabled={!itemData.item_name || itemData.unit_price <= 0}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar ao Orçamento
        </button>
      </div>
    </div>
  );
};

export default CortesPdfToBudget;
